import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Checkbox, MenuItem, FormControl, Select, Box, Tooltip, Chip } from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ArticleIcon from '@mui/icons-material/Article'; // For Transcript
import AssessmentIcon from '@mui/icons-material/Assessment'; // For Report
import { useSearchParams } from 'react-router-dom';
import { UserContext } from './UserContext';
import ScheduleInterviewUI from './ScheduleInterviewUI';
import InterviewAudioPopup from './InterviewAudioPopup';
import apiFetch from '../utils/apiFetch';
import './InterviewDetails.css'

const InterviewDetails = (props) => {
  const [searchParams] = useSearchParams();
  const { user } = useContext(UserContext);

  // States
  const [interview, setInterview] = useState([]);
  const [OpenShedule, setOpenScedule] = useState(false);
  const [hrId, setHRId] = useState('all');
  const [hrName, setHRName] = useState('All');
  const [selectedName, setSelectedName] = useState('');
  const [selectedHRName, setSelectedHRName] = useState('');
  const [selectedRow, setSelectedRow] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [hrNames, setHRNames] = useState([{ _id: 'all', name: "All", count: "" }]);
  const [showSpoken, setShowSpoken] = useState(false);
  const [showNotSpoken, setShowNotSpoken] = useState(true);
  const [filteredRowsCount, setFilteredRowsCount] = useState(0);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [fromDate, setFromDate] = useState(new Date(props?.filter === "Scheduled" ? Date.now() - 15 * 24 * 60 * 60 * 1000 : new Date()));
  const [toDate, setToDate] = useState(new Date().toISOString().substring(0, 10));
  const [filterModel, setFilterModel] = useState({ items: [] });

  // --- Audio States ---
  const [audioPopupOpen, setAudioPopupOpen] = useState(false);
  const [selectedAudioInterviewId, setSelectedAudioInterviewId] = useState(null);

  // --- Hardware Permissions States ---
  const [micAllowed, setMicAllowed] = useState(true);
  const micAllowedRef = useRef(true);

  const [pageState, setPageState] = useState({
    isLoading: false,
    page: 1,
    pageSize: 100
  });

  // State for Modals
  const [transcriptModal, setTranscriptModal] = useState({ open: false, data: null, name: "" });
  const [reportModal, setReportModal] = useState({ open: false, data: null, name: "" });

  // Helper to determine Report button background color
  const getReportColor = (reportText) => {
    if (!reportText) return '#e0e0e0'; // Default Gray

    const text = String(reportText).toLowerCase();
    const hasPass = text.includes('pass');
    const hasFail = text.includes('fail');

    if (hasPass && hasFail) return '#ff9800'; // Orange (Mixed)
    if (hasPass && !hasFail) return '#4caf50'; // Green (All Pass)
    if (!hasPass && hasFail) return '#f44336'; // Red (All Fail)

    return '#9e9e9e'; // Gray fallback if neither word is found
  };

  // --- Helper: Render Chat-like Transcript ---
  const renderTranscriptUI = (text, candidateName) => {
    if (!text) return <Typography>No transcript available.</Typography>;

    const lines = text.split('\n');
    const messages = [];
    let currentSpeaker = "System";
    let currentMessage = [];

    // Improved Regex: Allows leading spaces and handles names with spaces/parentheses
    // e.g., "Interviewer:" or "John (Candidate):"
    const speakerRegex = /^\s*([A-Za-z0-9 _().-]+):\s*(.*)/;

    lines.forEach(line => {
      const match = line.match(speakerRegex);
      if (match) {
        // Save the previous speaker's message block before starting a new one
        if (currentMessage.length > 0) {
          messages.push({ speaker: currentSpeaker, text: currentMessage.join('\n') });
        }
        currentSpeaker = match[1].trim();
        currentMessage = [match[2].trim()];
      } else {
        // If no speaker label is found, it's a continuation of the previous message
        if (line.trim() !== '') {
          currentMessage.push(line.trim());
        }
      }
    });

    // Push the very last message in the loop
    if (currentMessage.length > 0) {
      messages.push({ speaker: currentSpeaker, text: currentMessage.join('\n') });
    }

    const safeCandidateName = candidateName ? String(candidateName).toLowerCase() : "candidate";

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1, p: 1 }}>
        {messages.map((msg, index) => {
          const isCandidate =
            msg.speaker.toLowerCase().includes('candidate') ||
            msg.speaker.toLowerCase().includes(safeCandidateName) ||
            msg.speaker.toLowerCase() === 'user';

          return (
            <Box key={index} sx={{ display: 'flex', justifyContent: isCandidate ? 'flex-end' : 'flex-start' }}>
              <Box sx={{ maxWidth: '85%' }}>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1, mr: 1, mb: 0.5,
                    color: 'text.secondary',
                    display: 'block',
                    textAlign: isCandidate ? 'right' : 'left',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.7rem'
                  }}
                >
                  {isCandidate ? (candidateName || 'Candidate') : msg.speaker}
                </Typography>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: isCandidate ? '#e3f2fd' : '#ffffff',
                    color: isCandidate ? '#0d47a1' : '#333333',
                    border: isCandidate ? 'none' : '1px solid #e0e0e0',
                    borderBottomRightRadius: isCandidate ? 4 : 24,
                    borderBottomLeftRadius: !isCandidate ? 4 : 24,
                    whiteSpace: 'pre-wrap' // Ensures newlines are respected inside the bubble
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {msg.text}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // --- Helper: Render Beautiful JSON Report ---
  const renderReportUI = (reportData) => {
    if (!reportData) return <Typography>No report available.</Typography>;

    let data;
    try {
      data = typeof reportData === 'string' ? JSON.parse(reportData) : reportData;
    } catch (e) {
      return <Typography sx={{ whiteSpace: 'pre-wrap' }}>{reportData}</Typography>;
    }

    // Determine if a value is a pass/fail indicator and return the respective color
    const getBadgeColor = (val) => {
      const text = String(val).toLowerCase().trim();
      if (['pass', 'passed', 'yes', 'true', 'recommended', 'good', 'excellent'].includes(text)) {
        return { bg: '#e8f5e9', color: '#2e7d32' }; // Green
      }
      if (['fail', 'failed', 'no', 'false', 'not recommended', 'bad', 'poor', 'rejected'].includes(text)) {
        return { bg: '#ffebee', color: '#c62828' }; // Red
      }
      if (['average', 'maybe', 'pending', 'neutral'].includes(text)) {
        return { bg: '#fff3e0', color: '#ef6c00' }; // Orange
      }
      return null;
    };

    const renderNode = (value) => {
      // 1. Array Handler
      if (Array.isArray(value)) {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {value.map((item, i) => (
              <Chip key={i} label={String(item)} size="small" sx={{ bgcolor: '#f0f4c3', color: '#827717', fontWeight: 600 }} />
            ))}
          </Box>
        );
      }

      // 2. Object (Nested) Handler
      if (value !== null && typeof value === 'object') {
        return (
          <Box sx={{ pl: 2, borderLeft: '3px solid #e0e0e0', mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {Object.entries(value).map(([k, v]) => (
              <Box key={k}>
                <Typography variant="caption" sx={{ color: '#757575', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                  {k.replace(/_/g, ' ')}
                </Typography>
                <Box>{renderNode(v)}</Box>
              </Box>
            ))}
          </Box>
        );
      }

      // 3. Primitive (String/Number/Boolean) Handler
      const badgeStyle = getBadgeColor(value);

      // If it's a known status word, render a colored badge
      if (badgeStyle) {
        return (
          <Chip
            label={String(value).toUpperCase()}
            size="small"
            sx={{
              mt: 0.5,
              bgcolor: badgeStyle.bg,
              color: badgeStyle.color,
              fontWeight: 'bold',
              borderRadius: 1
            }}
          />
        );
      }

      // Otherwise, render standard text (highlight numbers slightly)
      const isScore = !isNaN(value) && String(value).length <= 3;
      return (
        <Typography variant="body1" sx={{
          mt: 0.5,
          fontWeight: isScore ? 'bold' : '500',
          color: isScore ? '#1976d2' : '#333333',
          fontSize: isScore ? '1.2rem' : '0.95rem'
        }}>
          {String(value)}
        </Typography>
      );
    };

    // Main Report Container
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 3, p: 1 }}>
        {Object.entries(data).map(([key, value]) => (
          <Paper
            key={key}
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: '#ffffff',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
            }}
          >
            <Typography variant="subtitle2" sx={{
              textTransform: 'uppercase',
              color: '#1976d2',
              mb: 2,
              borderBottom: '2px solid #e3f2fd',
              pb: 1,
              fontWeight: 800,
              letterSpacing: '1px'
            }}>
              {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
            </Typography>
            {renderNode(value)}
          </Paper>
        ))}
      </Box>
    );
  };

  const formatDate = (d) => d ? new Date(d).toISOString().split("T")[0] : "";
  // PROTECTED Mapping Logic
  const rowData = React.useMemo(() => {
    if (!Array.isArray(interview)) return [];

    return interview.map((item) => {
      if (!item) return null;

      const job = item.jobId || {};

      let parts = [];
      if (job.companyname) parts.push(job.companyname);
      if (job.officeName) parts.push(job.officeName);
      const companyName = parts.length > 0 ? parts.join(' - ') : "N/A";

      return {
        id: item._id || Math.random().toString(),
        applicantid: item.applicantId,
        jobid: item._id,
        interviewdate: item.interviewdate,
        interviewtime: item.interviewtime,
        name: item.name || "N/A",
        mobile: item.mobile || "",
        status: item.status || "",
        companyname: companyName,
        jobtitle: item.jobId.jobtitle || "",
        interviewername: item.interviewername || "",
        summary: item.summary || "",
        transcript: item.transcript || "",
        report: item.report || "",
        hrname: item.hrname || "",
        timespent: item.timeSpent || 0,
        feedback: item.feedback || "",
        rounds: item.rounds || "",
        interviewdate: item.interviewdate ? new Date(item.interviewdate) : null,
        interviewtime: item.interviewtime || "",
        interviewlocation: item.location || "Mobile Call",
        area: item.location || "",
        englishrating: item.englishRating || "",
        communicationrating: item.communicationRating || "",
        bookmark: !!item.bookmark,
        bookmarkcomment: item.bookmarkcomments || "",
        hasAudio: !!item.hasAudio
      };
    }).filter(Boolean);
  }, [interview]);

  // Audio Button Handler
  const handleOpenAudioPopup = useCallback(({ id, name, hrname }) => {
    setSelectedAudioInterviewId(id);
    setSelectedName(name);
    setSelectedHRName(hrname);
    setAudioPopupOpen(true);
  }, []);

  const cols = [
    {
      field: 'action',
      headerName: '',
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex' }}>
          <Button size='small' title="View Candidate Details" onClick={() => { window.isIntentionalRedirect = true; window.location.href = "/applicants?applicantid=" + params.row.applicantid; }}>
            <PersonSearchIcon />
          </Button>
          {params.row.hasAudio && (
            <Button size='small' title="View Audio Recordings" onClick={() => handleOpenAudioPopup(params.row)}>
              <AudiotrackIcon color="primary" />
            </Button>
          )}
        </Box>
      ),
    },
    {
      field: 'interviewdate',
      headerName: 'Interview On',
      width: 190,
      renderCell: (params) => {
        const date = params.row.interviewdate
          ? new Date(params.row.interviewdate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
          : '';

        const time = params.row.interviewtime || '';

        return `${date}${time ? ` ${time}` : ''}`;
      }
    },
    { field: 'name', headerName: 'Name', width: 120 },
    { field: 'mobile', hide: user.accesstype != 5, headerName: 'Mobile No', width: 140 },
    { field: 'status', headerName: 'Status', width: 120 },
    {
      field: 'feedback',
      headerName: 'Feedback',
      width: 180,
      renderCell: (params) => {
        const feedback = params.value ?? "";

        const latestFeedback = feedback.includes(']:')
          ? feedback.split(']:')[1].split(/\s+[A-Za-z ]+\s\[/)[0].trim()
          : feedback;

        return (
          <Tooltip
            title={
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {latestFeedback}
              </div>
            }
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '22px',
                  padding: '10px 14px',
                },
              },
            }}
            arrow
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                display: 'block',
                cursor: 'pointer'
              }}
            >
              {latestFeedback}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'summary',
      headerName: 'AI Summary & Details',
      minWidth: 260, // Widened to fit the buttons
      resizable: true,
      renderCell: (params) => {
        const summary = params.row.summary ?? "";
        const transcript = params.row.transcript ?? "";
        const report = params.row.report ?? "";
        const candidateName = params.row.name ?? "Candidate";

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            {/* AI Summary Text & Tooltip */}
            <Tooltip
              title={<div style={{ whiteSpace: 'pre-wrap' }}>{summary || "No summary available"}</div>}
              slotProps={{
                tooltip: { sx: { fontSize: '18px', padding: '10px 14px' } },
              }}
              arrow
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                  cursor: 'pointer'
                }}
              >
                {summary || "-"}
              </span>
            </Tooltip>

            {/* Action Buttons for Transcript & Report */}
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              {transcript && (
                <Tooltip title="View Transcript" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setTranscriptModal({ open: true, data: transcript, name: candidateName })}
                    sx={{
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      boxShadow: 1,
                      '&:hover': { bgcolor: '#bbdefb' }
                    }}
                  >
                    <ArticleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {report && (
                <Tooltip title="View Report" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setReportModal({ open: true, data: report, name: candidateName })}
                    sx={{
                      bgcolor: getReportColor(report),
                      color: '#fff',
                      boxShadow: 1,
                      '&:hover': { filter: 'brightness(0.9)' }
                    }}
                  >
                    <AssessmentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      }
    },
    // { field: "area", headerName: "Location", width: 140 },
    {
      field: 'timespent',
      headerName: 'Time Spent - ' + (rowData && rowData.length > 0 ?
        (() => {
          const totalSeconds = rowData.reduce((prv, curr) =>
            (curr && curr.timespent && parseInt(curr.timespent) > 0) ? prv + parseInt(curr.timespent) : prv, 0);
          const h = String(parseInt(totalSeconds / 3600)).padStart(2, '0');
          const m = String(parseInt((totalSeconds / 60) % 60)).padStart(2, '0');
          const s = String(totalSeconds % 60).padStart(2, '0');
          return `${h}:${m}:${s}`;
        })() : "00:00:00"),
      width: 190,
      renderCell: (params) => {
        const ts = parseInt(params.row?.timespent);
        if (!ts || ts <= 0) return "00:00:00";
        return String(parseInt(ts / 3600)).padStart(2, '0') + ":" + String(parseInt((ts / 60) % 60)).padStart(2, '0') + ":" + String(ts % 60).padStart(2, '0');
      },
    },
    {
      field: 'englishrating', headerName: 'English Rating', width: 180,
      renderCell: (params) => {
        const ratings = { "1": "E - Pathetic", "2": "D - Bad", "3": "C - Average", "4": "B - Good", "5": "A - Excellent", "NaN": "Not Rated" };
        return [ratings[params.row.englishrating] || ""];
      },
    },
    {
      field: 'communicationrating', headerName: 'Communication Rating', width: 180,
      renderCell: (params) => {
        const ratings = { "1": "E - Pathetic", "2": "D - Bad", "3": "C - Average", "4": "B - Good", "5": "A - Excellent", "NaN": "Not Rated" };
        return [ratings[params.row.communicationrating] || ""];
      },
    },
    { field: 'hrname', headerName: 'HR Name', width: 190 },
    { field: 'rounds', headerName: 'Round', width: 150 },
    { field: 'companyname', headerName: 'Company Name', width: 200 },
    { field: 'jobtitle', headerName: 'Job Title', width: 200 },
    // { field: 'interviewername', headerName: 'Interviewer Name', width: 190 },
    // {
    //   field: 'interviewdate', headerName: 'Interview Date', type: 'date',
    //   valueGetter: ({ value }) => value && new Date(value).toDateString(), width: 140
    // },
    // { field: 'interviewlocation', headerName: 'Interview Location', width: 200 },
    {
      field: 'bookmark', headerName: 'Bookmarked', width: 200, renderCell: (params) => (
        <div>
          <Checkbox disabled checked={params.row.bookmark} />{params.row.bookmarkcomment}
        </div>
      ),
    }
  ];

  async function getUsers() {
    let apiURL = process.env.REACT_APP_API_URL + "/users/getusers"
    let apiParams = {
      method: "GET",
      headers: {
        authtoken: localStorage.getItem("loggedintoken")
      }
    };
    await apiFetch(apiURL, apiParams)
      .then((res) => (res.status === 200 ? res.json() : res.text()))
      .then((repos) => {
        if (repos.data) {
          const hrs = [...new Set([...hrNames, ...repos.data])];
          setHRNames(hrs)
        } else props.showAlert("90" + (repos.message || repos), "error");
      })
      .catch((err) => props.showAlert(('53' + err), "error"));
  }

  // API Call logic
  const getInterviews = useCallback(async () => {
    setPageState(old => ({ ...old, isLoading: true }));
    let apiURL = "";
    if (searchParams.get('applicantid')) {
      apiURL = process.env.REACT_APP_API_URL + "/interview/applicantinterview/" + searchParams.get('applicantid') + `?page=${pageState.page}&limit=${pageState.pageSize}`;
    } else if (searchParams.get('jobid')) {
      apiURL = process.env.REACT_APP_API_URL + "/interview/getjobinterviews/" + searchParams.get('jobid') + `?page=${pageState.page}&limit=${pageState.pageSize}`;
    } else {
      apiURL = process.env.REACT_APP_API_URL + "/interview/" + user._id + `?hr=${hrName}&mode=${showSpoken && showNotSpoken ? 'all' : showSpoken ? 'used' : 'fresh'}&fromdate=${formatDate(fromDate)}&todate=${formatDate(toDate)}&page=${pageState.page}&limit=${pageState.pageSize}` + (props?.filter ? '&filter=' + props.filter : '');
    }

    try {
      const res = await fetch(apiURL, { headers: { authtoken: localStorage.getItem("loggedintoken") } });
      const repos = await res.json();
      if (repos.data) {
        setInterview(repos.data.interview || []);
        setTotalRowsCount(repos.data.pagination?.count || 0);
      }
    } catch (err) {
      props.showAlert(('24' + err), "error");
    } finally {
      setPageState(old => ({ ...old, isLoading: false }));
    }
  }, [pageState.page, pageState.pageSize, showSpoken, showNotSpoken, hrName, fromDate, toDate, user._id, searchParams, props]);

  const getInterviewBySelectedCell = useCallback(async () => {
    setIsLoading(true);
    let apiURL = process.env.REACT_APP_API_URL + "/interview/dashboardValue/" + searchParams.get('selectedCell');
    try {
      const res = await fetch(apiURL, { headers: { authtoken: localStorage.getItem("loggedintoken") } });
      const repos = await res.json();
      setInterview(repos.data || []);
      setTotalRowsCount(repos.data?.length || 0);
    } catch (err) {
      props.showAlert(('36' + err), "error");
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, props]);

  useEffect(() => {
    if (user) {
      getUsers();
      if (Number(user.accesstype) >= 3) {
        setShowSpoken(true);
        setShowNotSpoken(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (searchParams.get('selectedCell')) {
      getInterviewBySelectedCell();
    } else {
      getInterviews();
    }
  }, [getInterviews, getInterviewBySelectedCell, searchParams]);

  // =========================================================
  // ✅ LIVE HARDWARE & PERMISSIONS MONITOR (Audio HR Only)
  // =========================================================
  const requestPermissionsManually = useCallback(async () => {
    if (Number(user?.accesstype) >= 3) return; // Admins don't need mic checks to view records

    let activeStream = null;
    let micPermissionStatus = null;

    const stopActiveStream = () => {
      if (activeStream) {
        activeStream.getTracks().forEach(t => {
          t.onmute = null;
          t.onunmute = null;
          t.onended = null;
          t.stop();
        });
        activeStream = null;
      }
    };

    const monitorHardware = async () => {
      try {
        stopActiveStream();
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        activeStream = stream;
        const track = stream.getAudioTracks()[0];

        const updateState = () => {
          const isLiveAndUnmuted = track.readyState === 'live' && !track.muted;
          setMicAllowed(isLiveAndUnmuted);
          micAllowedRef.current = isLiveAndUnmuted;
        };

        updateState();

        track.onmute = updateState;
        track.onunmute = updateState;

        track.onended = () => {
          setMicAllowed(false);
          micAllowedRef.current = false;
          stopActiveStream();
        };

      } catch (err) {
        setMicAllowed(false);
        micAllowedRef.current = false;
      }
    };

    try {
      micPermissionStatus = await navigator.permissions.query({ name: "microphone" });

      if (micPermissionStatus.state === 'granted') {
        monitorHardware();
      } else {
        setMicAllowed(false);
        micAllowedRef.current = false;

        if (micPermissionStatus.state === 'prompt') {
          monitorHardware();
        }
      }

      micPermissionStatus.onchange = () => {
        if (micPermissionStatus.state === 'granted') {
          monitorHardware();
        } else {
          setMicAllowed(false);
          micAllowedRef.current = false;
          stopActiveStream();
        }
      };
    } catch (err) {
      monitorHardware();
    }

    const handleDeviceChange = () => {
      if (micPermissionStatus?.state === 'granted' || !micPermissionStatus) {
        monitorHardware();
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      if (micPermissionStatus) micPermissionStatus.onchange = null;
      stopActiveStream();
    };
  }, [user?.accesstype]);

  useEffect(() => {
    const cleanup = requestPermissionsManually();
    return () => {
      cleanup.then(cleanFn => {
        if (typeof cleanFn === 'function') cleanFn();
      });
    };
  }, [requestPermissionsManually]);


  // =========================================================
  // ✅ AUTO-CLOSE SCHEDULE INTERVIEW IF MIC IS LOST
  // =========================================================
  useEffect(() => {
    if (Number(user?.accesstype) < 3 && !micAllowed && OpenShedule) {
      setOpenScedule(false);
      props.showAlert("Interview closed because microphone access was revoked or muted.", "error");
    }
  }, [micAllowed, OpenShedule, props, user?.accesstype]);


  // =========================================================
  // ✅ DOUBLE CLICK LOGIC
  // =========================================================
  const scheduleinterview = (row) => {
    // Prevent HR users from opening if they lack mic access
    if (Number(user?.accesstype) < 3 && !micAllowedRef.current) {
      props.showAlert("Cannot open interview. Microphone is muted or unavailable.", "error");
      return;
    }

    setSelectedRow(row);
    setOpenScedule(true);
  };

  return (
    <div style={{ position: "absolute", width: "100%", height: "calc(100% - 10px)" }}>
      {!(searchParams.get('applicantid') || searchParams.get('jobid') || searchParams.get('selectedCell')) && (
        <div style={{ position: "absolute", marginTop: "10px", right: "20px", display: "block", float: "right", zIndex: 2 }}>
          {props?.filter === 'Called' && <>Show:&nbsp;&nbsp;
            Spoken&nbsp;<input type="checkbox" checked={showSpoken} onChange={() => setShowSpoken(!showSpoken)} />
            &nbsp;&nbsp;
            Yet to Speak&nbsp;<input type="checkbox" checked={showNotSpoken} onChange={() => setShowNotSpoken(!showNotSpoken)} />
            &nbsp;&nbsp;</>}
          {parseInt(user.accesstype) >= 5 && (
            <>
              HR:&nbsp;
              <Select size="small" value={hrId} onChange={(e) => {
                setHRId(e.target.value);
                setHRName(hrNames.find(hr => hr._id === e.target.value).name);
              }}>
                {hrNames.map(hr => <MenuItem key={hr._id} value={hr._id}>{hr.name}</MenuItem>)}
              </Select>
            </>
          )}
          &nbsp;From&nbsp;<input type="date" value={formatDate(fromDate)} onChange={(e) => setFromDate(e.target.value)} />
          &nbsp;To&nbsp;<input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          &nbsp;Showing&nbsp;{filteredRowsCount} records
          <br /><br />
        </div>
      )}

      <div style={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={rowData}
          columns={cols}
          rowCount={totalRowsCount}
          loading={pageState.isLoading || isLoading}
          pagination
          paginationMode={searchParams.get('selectedCell') ? "client" : "server"}
          page={pageState.page - 1}
          pageSize={pageState.pageSize}
          onPageChange={(p) => setPageState(old => ({ ...old, page: p + 1 }))}
          onStateChange={(e) => setFilteredRowsCount(e.rowsMeta.positions.length)}
          components={{ Toolbar: GridToolbar }}
          onRowDoubleClick={(e) => scheduleinterview(e.row)}
        />
      </div>

      <ScheduleInterviewUI
        open={OpenShedule}
        interview={selectedRow}
        refresh={() => searchParams.get('selectedCell') ? getInterviewBySelectedCell() : getInterviews()}
        close={() => setOpenScedule(false)}
        showAlert={props.showAlert}
      />

      <InterviewAudioPopup
        open={audioPopupOpen}
        onClose={() => setAudioPopupOpen(false)}
        interviewId={selectedAudioInterviewId}
        intervieweeName={selectedName}
        hrName={selectedHRName}
        showAlert={props.showAlert}
      />

      {/* Warning Notification for HR Users if Mic is disconnected */}
      {Number(user?.accesstype) < 3 && !micAllowed && (
        <div style={{ color: 'yellow', backgroundColor: "red", textAlign: "center", padding: "8px", position: "fixed", bottom: 0, width: "100%", zIndex: 9999 }}>
          Please grant Microphone Permissions to process interviews!
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={requestPermissionsManually}
            sx={{ ml: 2, padding: "2px 10px", fontWeight: "bold" }}
          >
            Retry Access
          </Button>
        </div>
      )}

      {/* ---------------- TRANSCRIPT POPUP ---------------- */}
      <Dialog
        open={transcriptModal.open}
        onClose={() => setTranscriptModal({ ...transcriptModal, open: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArticleIcon color="primary" /> Interview Transcript
          </Typography>
          <IconButton onClick={() => setTranscriptModal({ ...transcriptModal, open: false })} size="small" sx={{ bgcolor: '#f5f5f5' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#ffffff', p: 3, overflowY: 'auto' }}>

          {/* Call our UI Builder Function */}
          {renderTranscriptUI(transcriptModal.data, transcriptModal.name)}

        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#fff', borderTop: '1px solid #eee' }}>
          <Button onClick={() => setTranscriptModal({ ...transcriptModal, open: false })} variant="contained" disableElevation sx={{ borderRadius: 2 }}>
            Close Transcript
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- REPORT POPUP ---------------- */}
      <Dialog
        open={reportModal.open}
        onClose={() => setReportModal({ ...reportModal, open: false })}
        maxWidth="lg" // Made larger to accommodate the dashboard layout
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh', bgcolor: '#f8f9fa' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ color: getReportColor(reportModal.data) }} /> AI Assessment Report: {reportModal.name}
          </Typography>
          <IconButton onClick={() => setReportModal({ ...reportModal, open: false })} size="small" sx={{ bgcolor: '#f5f5f5' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>

          {/* Call our UI Builder Function */}
          {renderReportUI(reportModal.data)}

        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#fff', borderTop: '1px solid #eee' }}>
          <Button onClick={() => setReportModal({ ...reportModal, open: false })} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
            Close Report
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default InterviewDetails;